// ============================================================================
// CoinCommandos API Service
// 백엔드 Swagger 스펙: https://mmstudio.co.kr:20444/api-docs
// ============================================================================

const API_BASE_URL = 'https://mmstudio.co.kr:20444';
export { API_BASE_URL };

// 경로 인코딩 헬퍼 (ticker / timeFrame 등)
const enc = (v) => encodeURIComponent(v);

// /api/v1/auth/*, /api/v1/user/*, /api/v1/admin/* 는 Bearer 인증 필수
const isAuthRequiredPath = (path) => {
  return (
    path.startsWith('/api/v1/auth/') ||
    path.startsWith('/api/v1/user') ||
    path.startsWith('/api/v1/admin/')
  );
};

// optional { date, time } → 쿼리스트링 빌더
const buildReportQuery = ({ date, time } = {}) => {
  const params = new URLSearchParams();
  if (date) params.append('date', date);
  if (time) params.append('time', time);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
};

// ============================================================================
// 토큰/쿠키 유틸리티
// ============================================================================

const getCookieValue = (name) => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
};

export const getAccessToken = () => {
  return getCookieValue('accessToken') || localStorage.getItem('accessToken') || null;
};

export const getRefreshToken = () => {
  return getCookieValue('refreshToken') || localStorage.getItem('refreshToken') || null;
};

export const isTokenExpired = (token) => {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return true;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));
    if (!payload.exp) return true;
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

export const isOAuthUser = () => {
  const token = getRefreshToken();
  if (!token) return false;
  return !isTokenExpired(token);
};

// ============================================================================
// 네이티브 RefreshToken 브릿지
// ============================================================================

export const getNativeRefreshToken = () => {
  return new Promise((resolve) => {
    const timeout = setTimeout(() => resolve(null), 3000);

    window.receiveRefreshToken = function (token) {
      clearTimeout(timeout);
      window.receiveRefreshToken = null;
      if (token) {
        document.cookie = `refreshToken=${encodeURIComponent(token)}; path=/; SameSite=None; Secure`;
        localStorage.setItem('refreshToken', token);
      }
      resolve(token || null);
    };

    if (window.webkit?.messageHandlers?.getRefreshToken) {
      window.webkit.messageHandlers.getRefreshToken.postMessage(null); // iOS
    } else if (window.getRefreshToken) {
      window.getRefreshToken.postMessage(''); // Android
    } else {
      clearTimeout(timeout);
      resolve(null);
    }
  });
};

export const updateNativeRefreshToken = (refreshToken) => {
  if (window.webkit?.messageHandlers?.updateNativeRefreshToken) {
    window.webkit.messageHandlers.updateNativeRefreshToken.postMessage(refreshToken); // iOS: plain JWT string
  } else if (window.updateNativeRefreshToken) {
    window.updateNativeRefreshToken.postMessage(refreshToken); // Android
  }
};

export const initNativeToken = async () => {
  const platform = getPlatform();
  if (platform !== 'ios' && platform !== 'android') return;
  await getNativeRefreshToken();
};

export const deleteNativeRefreshToken = () => {
  if (window.webkit?.messageHandlers?.deleteNativeRefreshToken) {
    window.webkit.messageHandlers.deleteNativeRefreshToken.postMessage(null);
  } else if (window.deleteNativeRefreshToken) {
    window.deleteNativeRefreshToken.postMessage('');
  }
};

// ============================================================================
// TabBar / 외부 URL / 리뷰 / 스토리지 브릿지
// ============================================================================

export const hideNativeTabBar = () => {
  if (window.webkit?.messageHandlers?.hideTabBar) {
    window.webkit.messageHandlers.hideTabBar.postMessage(null);
  } else if (window.hideTabBar) {
    window.hideTabBar.postMessage('');
  }
};

export const showNativeTabBar = () => {
  if (window.webkit?.messageHandlers?.showTabBar) {
    window.webkit.messageHandlers.showTabBar.postMessage(null);
  } else if (window.showTabBar) {
    window.showTabBar.postMessage('');
  }
};

export const openExternalUrl = (url) => {
  if (window.webkit?.messageHandlers?.openExternalUrl) {
    window.webkit.messageHandlers.openExternalUrl.postMessage(url);
  } else if (window.openExternalUrl) {
    window.openExternalUrl.postMessage(url);
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

export const requestAppReview = () => {
  if (window.webkit?.messageHandlers?.requestAppReview) {
    window.webkit.messageHandlers.requestAppReview.postMessage(null);
  } else if (window.requestAppReview) {
    window.requestAppReview.postMessage('');
  }
};

export const setNativeStorage = (key, value) => {
  const payload = JSON.stringify({ key, value });
  if (window.webkit?.messageHandlers?.setNativeStorage) {
    window.webkit.messageHandlers.setNativeStorage.postMessage(payload);
  } else if (window.setNativeStorage) {
    window.setNativeStorage.postMessage(payload);
  }
};

export const getNativeStorage = (key) => {
  return new Promise((resolve) => {
    const timeout = setTimeout(() => resolve(null), 3000);
    window.receiveNativeStorage = function (value) {
      clearTimeout(timeout);
      window.receiveNativeStorage = null;
      resolve(value ?? null);
    };
    const payload = JSON.stringify({ key });
    if (window.webkit?.messageHandlers?.getNativeStorage) {
      window.webkit.messageHandlers.getNativeStorage.postMessage(payload);
    } else if (window.getNativeStorage) {
      window.getNativeStorage.postMessage(payload);
    } else {
      clearTimeout(timeout);
      resolve(null);
    }
  });
};

// ============================================================================
// 플랫폼 / 네이티브 앱 버전 조회 (브릿지)
// ============================================================================

export const getPlatform = () => {
  const ua = navigator.userAgent || '';
  if (/CoinCommandos\/iOS/i.test(ua)) return 'ios';
  if (/CoinCommandos\/AOS/i.test(ua)) return 'android';
  return 'web';
};

// 네이티브 → window.receiveAppVersion(version) 콜백 형태
export const getAppVersion = () =>
  new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), 3000);
    window.receiveAppVersion = (version) => {
      clearTimeout(timer);
      window.receiveAppVersion = null;
      resolve(version || null);
    };
    if (window.webkit?.messageHandlers?.getAppVersion) {
      window.webkit.messageHandlers.getAppVersion.postMessage(null);
    } else if (window.getAppVersion) {
      window.getAppVersion.postMessage('');
    } else {
      clearTimeout(timer);
      resolve(null);
    }
  });

export const isVersionBelow = (version, minVersion) => {
  if (!version) return false;
  const a = version.split('.').map(Number);
  const b = minVersion.split('.').map(Number);
  for (let i = 0; i < 3; i++) {
    if ((a[i] || 0) < (b[i] || 0)) return true;
    if ((a[i] || 0) > (b[i] || 0)) return false;
  }
  return false;
};

// ============================================================================
// 토큰 리프레시 / 인증 fetch wrapper
// ============================================================================

let refreshPromise = null;

// POST /api/v1/open/auth/token — refreshToken으로 accessToken 재발급
export const refreshAccessToken = async () => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    // iOS: 네이티브 저장소에서 한 번 동기화 시도
    if (getPlatform() === 'ios' || getPlatform() === 'android') {
      const nativeToken = await getNativeRefreshToken().catch(() => null);
      if (nativeToken) {
        localStorage.setItem('refreshToken', nativeToken);
      }
    }

    const refreshToken = getRefreshToken();
    if (!refreshToken) throw new Error('No refresh token');

    const response = await fetch(`${API_BASE_URL}/api/v1/open/auth/token`, {
      method: 'POST',
      headers: {
        'accept': '*/*',
        'Authorization': `Bearer ${refreshToken}`
      }
    });

    if (!response.ok) throw new Error('Token refresh failed');

    const data = await response.json();

    document.cookie = `accessToken=${encodeURIComponent(data.accessToken)}; path=/; SameSite=None; Secure`;
    if (data.refreshToken) {
      document.cookie = `refreshToken=${encodeURIComponent(data.refreshToken)}; path=/; SameSite=None; Secure`;
      localStorage.setItem('refreshToken', data.refreshToken);
    }
    localStorage.setItem('accessToken', data.accessToken);

    const platform = getPlatform();
    if ((platform === 'ios' || platform === 'android') && data.refreshToken) {
      updateNativeRefreshToken(data.refreshToken);
    }

    return data.accessToken;
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};

// 모든 API 호출에 공통으로 사용하는 fetch wrapper
// - /auth/*, /user/*, /admin/* → Bearer 필수, 401 시 1회 재시도
// - /open/*                    → 인증 없이 호출 (단, 토큰 보유 시 forward)
const fetchWithAuth = async (url, options = {}) => {
  // URL에서 path 추출
  let path = url;
  try {
    const u = new URL(url);
    path = u.pathname;
  } catch {
    /* relative url */
  }
  const authRequired = isAuthRequiredPath(path);

  const buildHeaders = (token) => {
    const h = { 'accept': '*/*', ...(options.headers || {}) };
    if (token) {
      h['Authorization'] = `Bearer ${token}`;
    }
    return h;
  };

  const makeRequest = (token) => fetch(url, { ...options, headers: buildHeaders(token) });

  const initialToken = getAccessToken();
  const response = await makeRequest(initialToken);

  // 인증 필요 또는 토큰 만료 → 401 재시도
  if (response.status === 401 && (authRequired || initialToken)) {
    try {
      const newToken = await refreshAccessToken();
      return makeRequest(newToken);
    } catch {
      if (authRequired && isOAuthUser()) {
        window.deleteOAuthToken?.();
        window.location.href = '/login?reason=token_expired';
      }
      if (authRequired) throw new Error('Session expired');
      return response; // open API는 401이어도 그대로 반환
    }
  }

  return response;
};

// ============================================================================
// Native Interface Handlers (네이티브 → 웹 직접 호출)
// ============================================================================

if (typeof window !== 'undefined') {
  window.getNativeRefreshToken = getNativeRefreshToken;
  window.updateNativeRefreshToken = updateNativeRefreshToken;
  window.deleteNativeRefreshToken = deleteNativeRefreshToken;

  window.getOAuthToken = function () {
    return getCookieValue('accessToken');
  };

  window.updateOAuthToken = function (accessToken, refreshToken) {
    document.cookie = `accessToken=${encodeURIComponent(accessToken)}; path=/; SameSite=None; Secure`;
    localStorage.setItem('accessToken', accessToken);
    if (refreshToken) {
      document.cookie = `refreshToken=${encodeURIComponent(refreshToken)}; path=/; SameSite=None; Secure`;
      localStorage.setItem('refreshToken', refreshToken);
    }
  };

  window.deleteOAuthToken = function () {
    document.cookie = 'accessToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie = 'refreshToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('oauthLoggedIn');
  };

  window.hideNativeTabBar = hideNativeTabBar;
  window.showNativeTabBar = showNativeTabBar;
  window.requestAppReview = requestAppReview;
  window.getAppVersion = getAppVersion;
  window.setNativeStorage = setNativeStorage;
  window.getNativeStorage = getNativeStorage;

  window.navigateTo = function (path, direction) {
    const state = {};
    if (direction === 'back') state._navDirection = 'back';
    window.__navigate?.(path, { state });
  };
}

// ============================================================================
// 1. 검색 (Search) — /api/v1/open/search/*
// ============================================================================

// GET /api/v1/open/search?query={query}
export const searchCoins = async (query) => {
  const params = new URLSearchParams();
  if (query !== undefined && query !== null) params.append('query', query);
  const qs = params.toString();
  const response = await fetchWithAuth(
    `${API_BASE_URL}/api/v1/open/search${qs ? `?${qs}` : ''}`,
    { method: 'GET' }
  );
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return await response.json();
};

// GET /api/v1/open/search/tickers — 전체 티커 목록
export const fetchAllTickers = async () => {
  const response = await fetchWithAuth(
    `${API_BASE_URL}/api/v1/open/search/tickers`,
    { method: 'GET' }
  );
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return await response.json();
};
// 하위 호환 별칭
export const getSearchTickers = fetchAllTickers;

// ============================================================================
// 2. 즐겨찾기 (Favorites) — /api/v1/auth/favorite — 인증 필수
// ============================================================================

// GET /api/v1/auth/favorite
export const fetchFavorites = async () => {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/v1/auth/favorite`, {
    method: 'GET'
  });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return await response.json();
};
export const getFavoriteList = fetchFavorites; // 하위 호환

// PUT /api/v1/auth/favorite — body: ModifyFavoriteRequest { ticker }
export const toggleFavorite = async (ticker) => {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/v1/auth/favorite`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ticker })
  });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return await response.json();
};
// 하위 호환 별칭
export const addFavorite = async (ticker) => toggleFavorite(ticker);
export const deleteFavorite = async (ticker) => toggleFavorite(ticker);

// ============================================================================
// 3. 인증 (Auth) — /api/v1/open/auth/*
// ============================================================================

// GET /api/v1/open/auth/kakao/oauth — 카카오 OAuth 콜백
export const kakaoOAuthCallback = async (code, error = null, errorDescription = null) => {
  const params = new URLSearchParams();
  if (code) params.append('code', code);
  if (error) params.append('error', error);
  if (errorDescription) params.append('error_description', errorDescription);
  const response = await fetch(
    `${API_BASE_URL}/api/v1/open/auth/kakao/oauth?${params.toString()}`,
    { method: 'GET', headers: { 'accept': '*/*' }, credentials: 'include' }
  );
  if (!response.ok) throw new Error(`Kakao OAuth failed: ${response.status}`);
  return await response.json();
};

// POST /api/v1/open/auth/apple/oauth — 애플 OAuth 콜백
export const appleOAuthCallback = async (code, idToken = null, user = null) => {
  const body = new URLSearchParams();
  if (code) body.append('code', code);
  if (idToken) body.append('id_token', idToken);
  if (user) body.append('user', typeof user === 'string' ? user : JSON.stringify(user));
  const response = await fetch(
    `${API_BASE_URL}/api/v1/open/auth/apple/oauth`,
    {
      method: 'POST',
      headers: {
        'accept': '*/*',
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: body.toString(),
      credentials: 'include'
    }
  );
  if (!response.ok) throw new Error(`Apple OAuth failed: ${response.status}`);
  return await response.json();
};

// ============================================================================
// 4. 유저 (User) — /api/v1/user/* — 인증 필수
// ============================================================================

// GET /api/v1/user
export const fetchUser = async () => {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/v1/user`, {
    method: 'GET'
  });
  if (!response.ok) throw new Error('Failed to fetch user info');
  return await response.json();
};
export const getUserInfo = fetchUser; // 하위 호환

// HttpOnly 쿠키 기반 토큰 유효성 검증 (별도 유틸)
export const validateToken = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/user`, {
      method: 'GET',
      headers: { 'accept': '*/*' },
      credentials: 'include'
    });
    return response.ok;
  } catch {
    return false;
  }
};

// POST /api/v1/user/withdraw — 회원 탈퇴
export const withdrawUser = async () => {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/v1/user/withdraw`, {
    method: 'POST'
  });
  if (!response.ok) {
    throw new Error('회원 탈퇴에 실패했습니다.');
  }
  return response;
};

// ============================================================================
// 5. 에이전트 분석 리포트 (Reports)
//    GET /api/v1/open/report/{ticker}/{type}/{timeFrame}?date=&time=
//    type ∈ technical | community | risk | overall
// ============================================================================

const REPORT_TYPES = ['technical', 'community', 'risk', 'overall'];

export const fetchAgentReport = async (ticker, type, timeFrame = '1d', opts = {}) => {
  if (!REPORT_TYPES.includes(type)) {
    throw new Error(`Invalid report type: ${type} (allowed: ${REPORT_TYPES.join(', ')})`);
  }
  const tf = timeFrame || '1d';
  const qs = buildReportQuery(opts);
  const response = await fetchWithAuth(
    `${API_BASE_URL}/api/v1/open/report/${enc(ticker)}/${enc(type)}/${enc(tf)}${qs}`,
    { method: 'GET' }
  );
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return await response.json();
};

// 하위 호환 래퍼 — 기존 컴포넌트는 (symbol, timeFrame, runAt) 시그니처를 사용한다.
// runAt는 다음 중 하나일 수 있다:
//   1) /run-at API 응답 객체: { ticker, timeFrame, runAt: '2026-05-12T09:05:00', insertDate }
//   2) ISO 문자열: '2026-05-12T09:05:00'
//   3) 이미 분리된 객체: { date, time }
// 백엔드는 detail 엔드포인트에서 date+time 쿼리를 필수로 요구하므로
// 누락 시 detail이 400 BadRequest를 반환한다.
const splitRunAt = (runAt) => {
  if (!runAt) return {};
  if (typeof runAt === 'object') {
    // 이미 분리된 형태
    if (runAt.date || runAt.time) return { date: runAt.date, time: runAt.time };
    // /run-at 응답 형태 → 안쪽 ISO 문자열 재귀 파싱
    if (runAt.runAt) return splitRunAt(runAt.runAt);
    if (runAt.insertDate) return splitRunAt(runAt.insertDate);
    return {};
  }
  if (typeof runAt === 'string') {
    // 'YYYY-MM-DDTHH:mm[:ss]' or 'YYYY-MM-DD HH:mm' 패턴 가정
    const [datePart, timePart] = runAt.split(/[T ]/);
    return { date: datePart, time: timePart };
  }
  return {};
};

export const getTechnicalReport = async (symbol, timeFrame = '1d', runAt = null) =>
  fetchAgentReport(symbol, 'technical', timeFrame, splitRunAt(runAt));

export const getCommunityReport = async (symbol, timeFrame = '1d', runAt = null) =>
  fetchAgentReport(symbol, 'community', timeFrame, splitRunAt(runAt));

export const getRiskReport = async (symbol, timeFrame = '1d', runAt = null) =>
  fetchAgentReport(symbol, 'risk', timeFrame, splitRunAt(runAt));

export const getOverallReport = async (symbol, timeFrame = '1d', runAt = null) =>
  fetchAgentReport(symbol, 'overall', timeFrame, splitRunAt(runAt));

// 기존 코드 호환: 'overall' 리포트를 요약 리포트로 사용
export const getReport = getOverallReport;
export const getSummaryReport = getOverallReport;
export const getTechnicalSummaryReport = getTechnicalReport;
export const getCommunitySummaryReport = getCommunityReport;
export const getRiskSummaryReport = getRiskReport;

// 일부 컴포넌트는 *Analysis 이름을 그대로 사용 → 동일 엔드포인트로 매핑
export const getTechnicalAnalysis = getTechnicalReport;
export const getCommunityAnalysis = getCommunityReport;
export const getRiskAnalysis = getRiskReport;
// Sentiment / MarketRisk 는 Swagger에 별도 엔드포인트 없음 → community/risk 리포트로 폴백
export const getSentimentAnalysis = getCommunityReport;
export const getMarketRiskAnalysis = async (timeFrame = '1d', runAt = null) =>
  fetchAgentReport('MARKET', 'risk', timeFrame, splitRunAt(runAt));

// ============================================================================
// 6. 코인 상세 (Crypto Detail) — /api/v1/open/crypto-detail/{ticker}/*
//    경로: /summary/{tf}, /technical/{tf}, /risk/{tf}, /community/{tf}, /run-at/{tf}
// ============================================================================

const buildCryptoDetailUrl = (ticker, kind, timeFrame, opts = {}) => {
  const tf = timeFrame || '1d';
  const qs = buildReportQuery(opts);
  return `${API_BASE_URL}/api/v1/open/crypto-detail/${enc(ticker)}/${enc(kind)}/${enc(tf)}${qs}`;
};

export const fetchCryptoSummary = async (ticker, timeFrame = '1d', opts = {}) => {
  const response = await fetchWithAuth(buildCryptoDetailUrl(ticker, 'summary', timeFrame, opts), {
    method: 'GET'
  });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return await response.json();
};

export const fetchCryptoTechnical = async (ticker, timeFrame = '1d', opts = {}) => {
  const response = await fetchWithAuth(buildCryptoDetailUrl(ticker, 'technical', timeFrame, opts), {
    method: 'GET'
  });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return await response.json();
};

export const fetchCryptoRisk = async (ticker, timeFrame = '1d', opts = {}) => {
  const response = await fetchWithAuth(buildCryptoDetailUrl(ticker, 'risk', timeFrame, opts), {
    method: 'GET'
  });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return await response.json();
};

export const fetchCryptoCommunity = async (ticker, timeFrame = '1d', opts = {}) => {
  const response = await fetchWithAuth(buildCryptoDetailUrl(ticker, 'community', timeFrame, opts), {
    method: 'GET'
  });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return await response.json();
};

// GET /api/v1/open/crypto-detail/{ticker}/run-at/{timeFrame} — 최신 실행 시각
export const fetchCryptoRunAt = async (ticker, timeFrame = '1d') => {
  try {
    const tf = timeFrame || '1d';
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/v1/open/crypto-detail/${enc(ticker)}/run-at/${enc(tf)}`,
      { method: 'GET' }
    );
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching crypto run-at:', error);
    return null;
  }
};

// 하위 호환 별칭 — 기존 컴포넌트들이 (symbol, timeFrame, runAt) 시그니처 사용
export const getCoinDetailSummary = (symbol, timeFrame = '1d', runAt = null) =>
  fetchCryptoSummary(symbol, timeFrame, splitRunAt(runAt));
export const getCoinDetailTechnical = (symbol, timeFrame = '1d', runAt = null) =>
  fetchCryptoTechnical(symbol, timeFrame, splitRunAt(runAt));
export const getCoinDetailRisk = (symbol, timeFrame = '1d', runAt = null) =>
  fetchCryptoRisk(symbol, timeFrame, splitRunAt(runAt));
export const getCoinDetailCommunity = (symbol, timeFrame = '1d', runAt = null) =>
  fetchCryptoCommunity(symbol, timeFrame, splitRunAt(runAt));
export const getCoinDetailRunAt = (symbol, timeFrame = '1d') =>
  fetchCryptoRunAt(symbol, timeFrame);

// ============================================================================
// 7. 대시보드 (Dashboard) — /api/v1/open/dashboard/*
// ============================================================================

export const fetchDashboardMain = async () => {
  const response = await fetchWithAuth(
    `${API_BASE_URL}/api/v1/open/dashboard/dashboard-main-content`,
    { method: 'GET' }
  );
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return await response.json();
};
export const getDashboardMainContent = fetchDashboardMain;

export const fetchDashboardMarket = async () => {
  const response = await fetchWithAuth(
    `${API_BASE_URL}/api/v1/open/dashboard/dashboard-market-data`,
    { method: 'GET' }
  );
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return await response.json();
};
export const getDashboardMarketData = fetchDashboardMarket;

export const fetchDashboardNews = async () => {
  const response = await fetchWithAuth(
    `${API_BASE_URL}/api/v1/open/dashboard/dashboard-news-data`,
    { method: 'GET' }
  );
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return await response.json();
};
export const getDashboardNewsData = fetchDashboardNews;

export const fetchFearAndGreed = async () => {
  const response = await fetchWithAuth(
    `${API_BASE_URL}/api/v1/open/dashboard/fear-and-greed-index`,
    { method: 'GET' }
  );
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return await response.json();
};
export const getFearGreedIndex = fetchFearAndGreed;

export const fetchPolyMarket = async () => {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/v1/open/dashboard/poly-market`,
      { method: 'GET' }
    );
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching poly-market:', error);
    return null;
  }
};
export const getPolyMarket = fetchPolyMarket;

// GET /api/v1/open/dashboard/coin-ranking/{period} — period ∈ '8h' | '1d'
export const fetchCoinRanking = async (period = '1d') => {
  const path = period === '8h' ? '8h' : '1d';
  const response = await fetchWithAuth(
    `${API_BASE_URL}/api/v1/open/dashboard/coin-ranking/${path}`,
    { method: 'GET' }
  );
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return await response.json();
};
export const getCoinRanking = fetchCoinRanking;

// ============================================================================
// 8. 커뮤니티 (Community) — /api/v1/open/community/*
// ============================================================================

// GET /api/v1/open/community?cursor=...
export const fetchCommunity = async (cursor = null) => {
  const url = cursor
    ? `${API_BASE_URL}/api/v1/open/community?cursor=${encodeURIComponent(cursor)}`
    : `${API_BASE_URL}/api/v1/open/community`;
  const response = await fetchWithAuth(url, { method: 'GET' });
  if (!response.ok) throw new Error('Failed to fetch community posts');
  return await response.json();
};
export const getCommunityPosts = fetchCommunity;

// GET /api/v1/open/community/reply?communityNo=...
export const fetchCommunityReplies = async (communityNo) => {
  const response = await fetchWithAuth(
    `${API_BASE_URL}/api/v1/open/community/reply?communityNo=${encodeURIComponent(communityNo)}`,
    { method: 'GET' }
  );
  if (!response.ok) throw new Error('Failed to fetch replies');
  return await response.json();
};
export const getCommunityReplies = fetchCommunityReplies;

// ============================================================================
// 9. 앱 정보 — /api/v1/info/app-version
// ============================================================================

// GET /api/v1/info/app-version — 최소/현재 버전, 스토어 URLs
export const fetchAppVersion = async () => {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/v1/info/app-version`,
      { method: 'GET' }
    );
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching app version info:', error);
    return null;
  }
};

// ============================================================================
// 10. 관리자 (Admin) — /api/v1/admin/* — 관리자 인증 필수
// ============================================================================

// POST /api/v1/admin/x — X 포스트 추가
export const createAdminXPost = async (payload) => {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/v1/admin/x`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return await response.json();
};
